/**
 * UserPreferencesService — owns theme / language / notification toggles,
 * plus one-time UI flags (the recording pod's first-sight tutorial).
 *
 * Backed by a single doc at `users/{uid}/private/preferences`, which the
 * existing Firestore rule (owner + internal-admin only) already
 * protects. The doc is created on first write — until then the service
 * returns `DEFAULT_USER_PREFERENCES` so consumers (most notably
 * ThemeService) never have to special-case undefined.
 *
 * All mutations use `setDoc(..., { merge: true })` so concurrent edits
 * to other fields don't clobber each other.
 */

import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { doc, DocumentReference, onSnapshot, setDoc } from 'firebase/firestore';

import { AuthService } from '../firebase/auth.service';
import { FIREBASE_HANDLES } from '../firebase/firebase.providers';
import {
  DEFAULT_USER_PREFERENCES,
  type NotificationPreferences,
  type ThemePreference,
  type UserPreferences,
} from './account.types';

const VALID_THEMES: readonly ThemePreference[] = ['light', 'dark', 'system'];

@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly db = inject(FIREBASE_HANDLES).firestore;

  private readonly _preferences = signal<UserPreferences>(DEFAULT_USER_PREFERENCES);
  /** True once we've heard back from Firestore at least once. */
  private readonly _loaded = signal(false);

  readonly preferences = this._preferences.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly theme = computed(() => this._preferences().theme);
  readonly language = computed(() => this._preferences().language);
  readonly notifications = computed(() => this._preferences().notifications);
  readonly captureTutorialSeen = computed(() => this._preferences().captureTutorialSeen);

  private unsubscribe: (() => void) | null = null;

  constructor() {
    effect(() => {
      const user = this.auth.user();
      this.dispose();
      this._preferences.set(DEFAULT_USER_PREFERENCES);
      this._loaded.set(false);
      if (!user) return;
      this.subscribe(user.uid);
    });

    this.destroyRef.onDestroy(() => this.dispose());
  }

  async setTheme(theme: ThemePreference): Promise<void> {
    if (!VALID_THEMES.includes(theme)) {
      throw new Error(`Invalid theme: ${theme}`);
    }
    await this.persist({ theme });
  }

  async setLanguage(language: string): Promise<void> {
    const trimmed = language.trim();
    if (!trimmed) throw new Error('Language must be non-empty.');
    await this.persist({ language: trimmed });
  }

  async setNotificationPreference(
    key: keyof NotificationPreferences,
    enabled: boolean,
  ): Promise<void> {
    // Spread the current snapshot so we always write the full
    // notifications map — Firestore's deep-merge would otherwise
    // create a partial object that's awkward to read defensively.
    const current = this._preferences().notifications;
    await this.persist({ notifications: { ...current, [key]: enabled } });
  }

  /** Remember that the recording pod's first-sight tutorial has been shown. */
  async markCaptureTutorialSeen(): Promise<void> {
    await this.persist({ captureTutorialSeen: true });
  }

  private async persist(patch: Partial<UserPreferences>): Promise<void> {
    const user = this.auth.user();
    if (!user) throw new Error('Not signed in.');
    // Optimistic local update so the UI reflects the choice
    // immediately. If the write fails we restore the previous value
    // — the snapshot listener may not emit a corrective value
    // (offline, transient permission error) and we don't want the
    // ThemeService etc. to keep applying an unsaved preference.
    const previous = this._preferences();
    this._preferences.update((prev) => ({
      ...prev,
      ...patch,
      notifications: { ...prev.notifications, ...(patch.notifications ?? {}) },
    }));
    try {
      await setDoc(this.prefsDoc(user.uid), patch, { merge: true });
    } catch (err) {
      this._preferences.set(previous);
      throw err;
    }
  }

  private prefsDoc(uid: string): DocumentReference {
    return doc(this.db, 'users', uid, 'private', 'preferences');
  }

  private subscribe(uid: string): void {
    this.unsubscribe = onSnapshot(
      this.prefsDoc(uid),
      (snap) => {
        const data = (snap.data() ?? {}) as Record<string, unknown>;
        this._preferences.set(coerce(data));
        this._loaded.set(true);
      },
      () => {
        // Permission errors should never happen for the owner — the
        // existing /private/{docId} rule grants `owner || internal`.
        // Reset to defaults so the UI is still functional.
        this._preferences.set(DEFAULT_USER_PREFERENCES);
        this._loaded.set(true);
      },
    );
  }

  private dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
}

/**
 * Coerce a raw Firestore document into a fully-shaped {@link
 * UserPreferences} record, applying defaults for missing/invalid fields.
 *
 * Validation is intentionally minimal — we trust our own writes — but
 * we sanity-check the theme enum so a stray value can't break
 * ThemeService's switch.
 */
function coerce(raw: Record<string, unknown>): UserPreferences {
  const theme: ThemePreference = VALID_THEMES.includes(raw['theme'] as ThemePreference)
    ? (raw['theme'] as ThemePreference)
    : DEFAULT_USER_PREFERENCES.theme;
  const language =
    typeof raw['language'] === 'string' && raw['language']
      ? (raw['language'] as string)
      : DEFAULT_USER_PREFERENCES.language;
  const rawNotif = (raw['notifications'] ?? {}) as Record<string, unknown>;
  const notifications: NotificationPreferences = {
    runCompleted:
      typeof rawNotif['runCompleted'] === 'boolean'
        ? (rawNotif['runCompleted'] as boolean)
        : DEFAULT_USER_PREFERENCES.notifications.runCompleted,
    runFailed:
      typeof rawNotif['runFailed'] === 'boolean'
        ? (rawNotif['runFailed'] as boolean)
        : DEFAULT_USER_PREFERENCES.notifications.runFailed,
  };
  const captureTutorialSeen = raw['captureTutorialSeen'] === true;
  return { theme, language, notifications, captureTutorialSeen };
}
